import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { 
  Briefcase, Search, Filter, BarChart, Plus,
  Users, Clock, CheckCircle, Calendar, MessageSquare,
  FileText, ChevronRight, AlertCircle
} from 'lucide-react';

const translations = {
  es: {
    title: 'Gestión de Proyectos',
    subtitle: 'Gestiona y supervisa proyectos y tareas',
    sections: {
      overview: 'Vista General',
      tasks: 'Tareas',
      timeline: 'Cronograma',
      team: 'Equipo',
      files: 'Archivos'
    },
    actions: {
      newProject: 'Nuevo proyecto',
      newTask: 'Nueva tarea',
      assignTask: 'Asignar tarea'
    },
    search: 'Buscar...',
    filters: 'Filtros',
    status: {
      active: 'Activo',
      pending: 'Pendiente',
      completed: 'Completado'
    }
  },
  en: {
    title: 'Project Management',
    subtitle: 'Manage and monitor projects and tasks',
    sections: {
      overview: 'Overview',
      tasks: 'Tasks',
      timeline: 'Timeline',
      team: 'Team',
      files: 'Files'
    },
    actions: {
      newProject: 'New Project',
      newTask: 'New Task',
      assignTask: 'Assign Task'
    },
    search: 'Search...',
    filters: 'Filters',
    status: {
      active: 'Active',
      pending: 'Pending',
      completed: 'Completed'
    }
  }
};

// Mock data
const mockProjects = [
  {
    id: 1,
    name: 'Website Redesign',
    description: 'Complete overhaul of company website',
    progress: 65,
    dueDate: '2025-04-15',
    status: 'active',
    team: 8,
    tasks: {
      total: 24,
      completed: 16
    }
  },
  {
    id: 2,
    name: 'Mobile App Development',
    description: 'New mobile app for customer engagement',
    progress: 30,
    dueDate: '2025-06-30',
    status: 'active',
    team: 6,
    tasks: {
      total: 32,
      completed: 10
    }
  }
];

const mockTasks = [
  {
    id: 1,
    title: 'Design homepage mockup',
    project: 'Website Redesign',
    assignee: 'Sarah Chen',
    dueDate: '2025-03-20',
    priority: 'high',
    status: 'in-progress'
  },
  {
    id: 2,
    title: 'Implement user authentication',
    project: 'Mobile App Development',
    assignee: 'Alex Kim',
    dueDate: '2025-03-25',
    priority: 'medium',
    status: 'pending'
  }
];

const ProjectManagementPage = () => {
  const [activeSection, setActiveSection] = useState('overview');
  const { language } = useLanguage();
  const t = translations[language];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t.title}</h1>
            <p className="text-foreground/70">{t.subtitle}</p>
          </div>
          <div className="flex space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
              <Plus className="h-4 w-4 mr-2" />
              {t.actions.newProject}
            </button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0 sm:space-x-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder={t.search}
              className="w-full pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
          </div>
          <div className="flex space-x-4">
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <Filter className="h-4 w-4 mr-2" />
              {t.filters}
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-foreground hover:bg-secondary/30">
              <BarChart className="h-4 w-4 mr-2" />
              Reports
            </button>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="border-b border-border mb-8">
          <nav className="-mb-px flex space-x-8">
            {Object.entries(t.sections).map(([key, value]) => (
              <button
                key={key}
                onClick={() => setActiveSection(key)}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeSection === key
                    ? 'border-accent text-accent'
                    : 'border-transparent text-foreground/70 hover:text-foreground hover:border-border'
                }`}
              >
                {value}
              </button>
            ))}
          </nav>
        </div>

        {/* Content Section */}
        {activeSection === 'overview' && (
          <div className="space-y-6">
            {mockProjects.map((project) => (
              <div
                key={project.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <Briefcase className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{project.name}</h3>
                      <p className="text-foreground/70">{project.description}</p>
                    </div>
                  </div>
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      project.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {project.status.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="mt-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-sm text-foreground/70">Progress</span>
                    <span className="text-sm font-medium text-foreground">{project.progress}%</span>
                  </div>
                  <div className="h-2 bg-secondary rounded-full">
                    <div
                      className="h-2 bg-accent rounded-full"
                      style={{ width: `${project.progress}%` }}
                    ></div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Due Date</p>
                    <p className="text-sm font-medium text-foreground">{project.dueDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Team</p>
                    <p className="text-sm font-medium text-foreground">{project.team} members</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Tasks</p>
                    <p className="text-sm font-medium text-foreground">
                      {project.tasks.completed}/{project.tasks.total}
                    </p>
                  </div>
                  <div className="flex justify-end">
                    <button className="inline-flex items-center text-accent hover:text-accent/90">
                      View Project
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeSection === 'tasks' && (
          <div className="space-y-6">
            {mockTasks.map((task) => (
              <div
                key={task.id}
                className="dark-card p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-accent" />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{task.title}</h3>
                      <p className="text-foreground/70">{task.project}</p>
                    </div>
                  </div>
                  <div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      task.priority === 'high'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {task.priority.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-4 gap-4">
                  <div>
                    <p className="text-sm text-foreground/70">Assignee</p>
                    <p className="text-sm font-medium text-foreground">{task.assignee}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Due Date</p>
                    <p className="text-sm font-medium text-foreground">{task.dueDate}</p>
                  </div>
                  <div>
                    <p className="text-sm text-foreground/70">Status</p>
                    <p className="text-sm font-medium text-foreground">{task.status}</p>
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button className="text-foreground/70 hover:text-foreground">
                      <MessageSquare className="h-5 w-5" />
                    </button>
                    <button className="text-foreground/70 hover:text-foreground">
                      <Calendar className="h-5 w-5" />
                    </button>
                    <button className="text-foreground/70 hover:text-foreground">
                      <AlertCircle className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectManagementPage;